import moment from 'moment'

/**
 * CRM 會員列表匯出的 CSV 組裝。
 *
 * 匯出以前由後端 `GET /m/group/user?csv=true` 一次產生整份檔案；會員數大時後端要把幾萬筆
 * 撈完才能回應，一條連線要跑一兩分鐘，會撞到前面 CloudFront / LB 的逾時，
 * 而且首列「總會員人數」在串流輸出時無法先寫（PM-2487）。
 * 改成前端用會員列表的分頁 API 把資料撈完，再在瀏覽器組成同一格式的檔案：
 * 每個請求都在幾秒內結束，總人數就是撈到的列數，可以照舊放在第一列。
 *
 * 輸出格式與後端版本相同：UTF-8 BOM、首列總人數、六欄表頭、每列一位會員、`\n` 換行，
 * 欄位含逗號 / 引號 / 換行或以空白開頭時加雙引號（同 Go encoding/csv 的規則）。
 */

/** 每頁向後端撈的會員數 */
export const EXPORT_PAGE_SIZE = 500

/** 與後端 time.RFC3339 相同：不帶毫秒、帶時區位移 */
const RFC3339 = 'YYYY-MM-DDTHH:mm:ssZ'

/**
 * @typedef CrmMemberCsvLabels
 * @property {(total: number) => string} totalMembers 首列文字，例如 `總會員人數: 123`
 * @property {string} name
 * @property {string} phone
 * @property {string} total
 * @property {string} bought
 * @property {string} latestDate
 * @property {string} joinedAt
 */

/**
 * 依 Go encoding/csv 的規則決定是否加引號：空字串不加；含分隔符、引號、換行或以空白開頭才加。
 * @param {string} value
 * @returns {string}
 */
export function csvField(value) {
  const s = value == null ? '' : String(value)
  if (s === '') return ''
  const needsQuotes = s === '\\.' || /[",\r\n]/.test(s) || /^\s/.test(s)
  if (!needsQuotes) return s
  return `"${s.replace(/"/g, '""')}"`
}

/**
 * 金額照後端 decimal.NewFromFloat(x).String() 的輸出：最短十進位表示，沒有多餘的 .0
 * @param {number} value
 * @returns {string}
 */
export function formatAmount(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '0'
  return String(n)
}

/**
 * @param {string | null | undefined} value ISO 時間字串
 * @returns {string} 本地時間的 RFC3339，空值回空字串
 */
export function formatDate(value) {
  if (!value) return ''
  const m = moment(value)
  return m.isValid() ? m.format(RFC3339) : ''
}

/**
 * 把一位會員轉成一列。最近消費日期取 orders[0]（後端已排好，最近一筆在前）。
 * @param {object} member 會員列表 API 回傳的單筆資料
 * @returns {string[]}
 */
export function memberToRow(member) {
  const latest = member.orders && member.orders.length > 0 ? member.orders[0].createdAt : null
  return [
    member.name ?? '',
    member.phone ?? '',
    formatAmount(member.total),
    String(Math.trunc(Number(member.bought) || 0)),
    formatDate(latest),
    formatDate(member.joinedAt),
  ]
}

/**
 * @param {object[]} members 全部會員（分頁撈完後合併）
 * @param {CrmMemberCsvLabels} labels
 * @returns {string} 含 UTF-8 BOM 的 CSV 內容
 */
export function buildCrmMemberCsv(members, labels) {
  const lines = [
    [labels.totalMembers(members.length)],
    [labels.name, labels.phone, labels.total, labels.bought, labels.latestDate, labels.joinedAt],
    ...members.map(memberToRow),
  ]
  return '﻿' + lines.map((row) => row.map(csvField).join(',')).join('\n') + '\n'
}
